import type { BatchQuery, TransactionQuery } from '@/api/inventory.api';
import type { MedicineSearchQuery } from '@/api/catalog.api';
import type { Uuid } from '@/api/types';

/**
 * One place every cache key is spelled, so an invalidation after a write cannot
 * miss a list because a page typed its key slightly differently.
 *
 * Everything a pharmacy sees is scoped to one pharmacy, and the id lives in the
 * *prefix* rather than inside the filter object: a user with two branches who
 * switches between them must not see the other branch's stock for the frame
 * before the refetch lands.
 */
export const queryKeys = {
  me: ['auth', 'me'] as const,

  medicines: (query: MedicineSearchQuery) => ['medicines', query] as const,
  medicine: (id: Uuid) => ['medicine', id] as const,

  pharmacy: (pharmacyId: Uuid) => ['pharmacy', pharmacyId] as const,
  /** Drug licences on file, for the printed bill's letterhead. */
  licenses: (pharmacyId: Uuid) => ['pharmacy', pharmacyId, 'licenses'] as const,

  prescriptions: {
    all: ['prescriptions'] as const,
    queue: (query: Record<string, unknown>) => ['prescriptions', 'queue', query] as const,
    /*
     * Outside `queue`, so a tab change does not refetch the counts.
     *
     * The numbers describe every tab at once and cannot narrow with the
     * selection, which means they are not a function of the filter — keying
     * them by it would throw away a valid cache entry on every tab press and
     * make the strip flicker back to blank.
     */
    queueFacets: ['prescriptions', 'queue', 'facets'] as const,
    detail: (id: Uuid) => ['prescriptions', 'detail', id] as const,
  },

  orders: {
    all: ['orders'] as const,
    queue: (query: Record<string, unknown>) => ['orders', 'queue', query] as const,
    /** Per branch, and per branch only — see `queueFacets` above for the rest. */
    queueFacets: (pharmacyId: Uuid) => ['orders', 'queue', 'facets', pharmacyId] as const,
    leg: (legId: Uuid) => ['orders', 'leg', legId] as const,
  },
  hours: (pharmacyId: Uuid) => ['pharmacy', pharmacyId, 'hours'] as const,
  channels: (pharmacyId: Uuid) => ['pharmacy', pharmacyId, 'channels'] as const,
  openNow: (pharmacyId: Uuid) => ['pharmacy', pharmacyId, 'open-now'] as const,
  closures: (pharmacyId: Uuid) => ['pharmacy', pharmacyId, 'closures'] as const,

  inventory: {
    scope: (pharmacyId: Uuid) => ['inventory', pharmacyId] as const,
    balances: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['inventory', pharmacyId, 'balances', query] as const,
    /*
     * Keyed by the SEARCH only. The tiles describe what you could filter to, so
     * they must not narrow with the filter already applied — keying them by
     * `stock` as well would refetch, and briefly blank, on every tile press.
     */
    balanceSummary: (pharmacyId: Uuid, q: string | undefined) =>
      ['inventory', pharmacyId, 'balances', 'summary', q ?? ''] as const,
    batches: (pharmacyId: Uuid, query: BatchQuery) =>
      ['inventory', pharmacyId, 'batches', query] as const,
    batchSummary: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['inventory', pharmacyId, 'batches', 'summary', query] as const,
    transactions: (pharmacyId: Uuid, query: TransactionQuery) =>
      ['inventory', pharmacyId, 'transactions', query] as const,
    ledgerSummary: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['inventory', pharmacyId, 'transactions', 'summary', query] as const,
    expiring: (pharmacyId: Uuid, withinDays: number) =>
      ['inventory', pharmacyId, 'expiring', withinDays] as const,
    lowStock: (pharmacyId: Uuid, threshold: number) =>
      ['inventory', pharmacyId, 'low-stock', threshold] as const,
  },

  counter: {
    scope: (pharmacyId: Uuid) => ['counter', pharmacyId] as const,
    search: (pharmacyId: Uuid, q: string) => ['counter', pharmacyId, 'search', q] as const,
    sales: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['counter', pharmacyId, 'sales', query] as const,
    sale: (pharmacyId: Uuid, saleId: Uuid) => ['counter', pharmacyId, 'sale', saleId] as const,
    dayBook: (pharmacyId: Uuid, date?: string) =>
      ['counter', pharmacyId, 'day-book', date ?? 'today'] as const,
  },

  /**
   * The register — both channels, one list. Scoped under `counter` would be
   * wrong: a void on a bill has to invalidate this too, and so does accepting
   * an online order, so it keeps its own prefix and both writers clear it.
   */
  register: {
    scope: (pharmacyId: Uuid) => ['register', pharmacyId] as const,
    list: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['register', pharmacyId, 'list', query] as const,
    /*
     * Keyed by the FILTER, not by the page. The strip describes everything the
     * filter matches and cannot narrow with the page you happen to be on —
     * keying it by `offset` would refetch, and briefly blank, every total on
     * every page turn.
     */
    summary: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['register', pharmacyId, 'summary', query] as const,
  },

  purchases: {
    scope: (pharmacyId: Uuid) => ['purchases', pharmacyId] as const,
    list: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['purchases', pharmacyId, 'list', query] as const,
    invoice: (pharmacyId: Uuid, invoiceId: Uuid) =>
      ['purchases', pharmacyId, 'invoice', invoiceId] as const,
    /* Scoped by pharmacy now the book is: a branch switch must not show the
       previous branch's suppliers for the frame before the refetch lands. */
    suppliers: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['suppliers', pharmacyId, query] as const,
    suppliersAll: ['suppliers'] as const,
    /* Keyed by the SEARCH only — the tiles say what you could filter to. */
    supplierSummary: (pharmacyId: Uuid, q: string | undefined) =>
      ['suppliers', pharmacyId, 'summary', q ?? ''] as const,
    summary: (pharmacyId: Uuid, query: Record<string, unknown>) =>
      ['purchases', pharmacyId, 'summary', query] as const,
    /** One supplier's invoices at this branch — the same list, filtered. */
    supplierInvoices: (pharmacyId: Uuid, supplierId: Uuid) =>
      ['purchases', pharmacyId, 'supplier', supplierId] as const,
  },

} as const;
